import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';

import { firstValueFrom } from 'rxjs';

import { ConfigurationService } from '../configuration/configuration.service';

import { LoggerService } from '../logger-services/logger.service';

import { BrandColorsApiResponse } from './brand-theme.models';



const DEFAULT_PRIMARY = '#295ef1';

const DEFAULT_SECONDARY = '#172b4d';

const BODY_BRAND_CLASS = 'freya-agents-brand';

/** How much white to mix into primary for hover/active pill backgrounds (0.9 ≈ former primary-100). */

const PRIMARY_LIGHT_WHITE_MIX = 0.9;



@Injectable({ providedIn: 'root' })

export class BrandThemeService {

  private readonly logTag = '[BrandThemeService]';

  private applied = false;
  private cachedColors: BrandColorsApiResponse | null = null;



  constructor(

    private readonly http: HttpClient,

    private readonly configService: ConfigurationService,

    private readonly loggerService: LoggerService

  ) {}



  /**

   * Fetches brand colors and applies them on the agents workspace host and document.body

   * (body vars reach CDK overlays; body class enables global agents SCSS overrides).

   */

  async loadAndApplyToAgents(agentsHost: HTMLElement, _availableAgentIds: string[] = []): Promise<void> {
    // Hub loads first; per-agent branding (and API fetch) runs on agent switch.
    await this.applyBrandingForAgent(agentsHost, false);
  }

  /**
   * Applies branding for the selected agent.
   * - brandingEnabled=true: apply API brand colors (fallback to defaults if API fails)
   * - brandingEnabled=false: force default design-system blue
   */
  async applyBrandingForAgent(agentsHost: HTMLElement, brandingEnabled: boolean): Promise<void> {
    if (!brandingEnabled) {
      this.applyColors(agentsHost, DEFAULT_PRIMARY, DEFAULT_SECONDARY);
      return;
    }

    const colors = await this.getBrandColors();
    this.applyColors(agentsHost, colors.primary, colors.secondary);
  }

  private async getBrandColors(): Promise<BrandColorsApiResponse> {
    if (this.cachedColors) {
      return this.cachedColors;
    }

    const baseUrl = this.removeTrailingSlashes(this.configService.getApiBaseUrl());

    const url = `${baseUrl}/api/brand-colors`;



    try {

      this.cachedColors = await firstValueFrom(

        this.http.get<BrandColorsApiResponse>(url, {

          headers: { accept: 'application/json' },

        })

      );

      return this.cachedColors;

    } catch (error) {

      this.loggerService.logError(

        `${this.logTag} ${JSON.stringify(error)}`,

        'loadAndApplyToAgents'

      );

      console.warn(`${this.logTag} Using default primary/secondary (API failed)`);
      return { primary: DEFAULT_PRIMARY, secondary: DEFAULT_SECONDARY };

    }
  }



  /** Remove body-level brand vars when leaving agents UI. */

  clearAgentsBrandFromBody(): void {

    document.body.classList.remove(BODY_BRAND_CLASS);

    for (const name of ['--brand-primary', '--brand-secondary', '--brand-primary-light']) {

      document.body.style.removeProperty(name);

    }

  }



  private removeTrailingSlashes(value: string): string {
    let normalized = value;
    while (normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }
    return normalized;
  }

  private applyColors(host: HTMLElement, primary: string, secondary: string): void {

    const primaryLight = this.lightenHex(primary, PRIMARY_LIGHT_WHITE_MIX);

    for (const el of [host, document.body]) {

      el.style.setProperty('--brand-primary', primary);

      el.style.setProperty('--brand-secondary', secondary);

      el.style.setProperty('--brand-primary-light', primaryLight);

    }

    document.body.classList.add(BODY_BRAND_CLASS);

    this.applied = true;

  }



  /** Mix primary toward white (e.g. 0.9 → light pill background on hover). */

  private lightenHex(hex: string, whiteMix: number): string {

    const { r, g, b } = this.parseHex(hex);

    const blend = (channel: number) =>

      Math.round(channel * (1 - whiteMix) + 255 * whiteMix);

    return this.toHex(blend(r), blend(g), blend(b));

  }



  private parseHex(hex: string): { r: number; g: number; b: number } {

    const normalized = hex.replace('#', '').trim();

    const value =

      normalized.length === 3

        ? normalized

            .split('')

            .map((c) => c + c)

            .join('')

        : normalized.slice(0, 6);

    return {

      r: parseInt(value.slice(0, 2), 16),

      g: parseInt(value.slice(2, 4), 16),

      b: parseInt(value.slice(4, 6), 16),

    };

  }



  private toHex(r: number, g: number, b: number): string {

    return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;

  }



  isApplied(): boolean {

    return this.applied;

  }

}

